import { create } from "zustand";
import api, { isAbortError } from "../../utils/api";
import type { Integrator } from "../reseller-admin/integratorsStore";

interface RetransmissionScope {
  type?: "all" | "devices" | "group";
  device_ids?: string[];
  group_id?: string | null;
}

interface RetransmissionCredentials {
  ws_name?: string;
  customer_name?: string;
  customer_id?: string;
}

interface Retransmission {
  id: string;
  integrator_id: string | null;
  active: boolean;
  scope?: RetransmissionScope;
  integrator_credentials?: RetransmissionCredentials;
}

export interface RetransmissionOption {
  id: string;
  integrator_id: string | null;
  adapter: string | null;
  ws_name: string;
  customer_name: string;
  label: string;
  coversAllDevices: boolean;
  device_ids: string[];
}

interface CompanyIntegratorsStore {
  integrators: Integrator[];
  retransmissions: RetransmissionOption[];
  hasRetransmissions: boolean;
  coversAllDevices: boolean;
  coveredDeviceIds: Set<string>;
  loading: boolean;
  loadedCompanyId: string | null;
  fetchCompanyIntegrators: (companyId: string) => Promise<void>;
  reset: () => void;
}

const emptyCoverage = {
  retransmissions: [] as RetransmissionOption[],
  hasRetransmissions: false,
  coversAllDevices: false,
  coveredDeviceIds: new Set<string>(),
};

export const useCompanyIntegratorsStore = create<CompanyIntegratorsStore>(
  (set, get) => ({
    integrators: [],
    ...emptyCoverage,
    loading: false,
    loadedCompanyId: null,

    fetchCompanyIntegrators: async (companyId: string) => {
      if (!companyId) return;
      const { loading, loadedCompanyId } = get();
      if (loading) return;
      if (loadedCompanyId === companyId) return;

      set({ loading: true, integrators: [], ...emptyCoverage });
      try {
        const [retransRes, catalogRes] = await Promise.all([
          api.get("/companyRetransmissionsList", {
            params: { company_id: companyId, limit: 200, page: 1, active: "true" },
          }),
          api.get("/integratorList", { params: { limit: 200, page: 1 } }),
        ]);

        const retransmissions: Retransmission[] = retransRes.data?.data || [];
        const catalog: Integrator[] = catalogRes.data?.data || [];
        const catalogById = new Map(catalog.map((it) => [it.id, it]));

        const seen = new Set<string>();
        const integrators: Integrator[] = [];
        const retransmissionOptions: RetransmissionOption[] = [];
        let coversAllDevices = false;
        const coveredDeviceIds = new Set<string>();
        let activeCount = 0;

        for (const r of retransmissions) {
          if (r.active === false) continue;
          activeCount += 1;

          if (!r.scope || r.scope.type === "all") {
            coversAllDevices = true;
          } else {
            for (const d of r.scope.device_ids || []) coveredDeviceIds.add(d);
          }

          const it = r.integrator_id
            ? catalogById.get(r.integrator_id)
            : undefined;
          const wsName = r.integrator_credentials?.ws_name || "";
          const customerName = r.integrator_credentials?.customer_name || "";
          const rCoversAll = !r.scope || r.scope.type === "all";
          retransmissionOptions.push({
            id: r.id,
            integrator_id: r.integrator_id ?? null,
            adapter: it?.adapter ?? null,
            ws_name: wsName,
            customer_name: customerName,
            label: wsName || it?.name || r.id,
            coversAllDevices: rCoversAll,
            device_ids: rCoversAll ? [] : r.scope?.device_ids || [],
          });

          if (!it || !it.adapter || seen.has(it.adapter)) continue;
          seen.add(it.adapter);
          integrators.push(it);
        }

        set({
          integrators,
          retransmissions: retransmissionOptions,
          hasRetransmissions: activeCount > 0,
          coversAllDevices,
          coveredDeviceIds,
          loadedCompanyId: companyId,
          loading: false,
        });
      } catch (err: unknown) {
        if (isAbortError(err)) {
          set({ loading: false });
          return;
        }
        set({
          integrators: [],
          ...emptyCoverage,
          loadedCompanyId: null,
          loading: false,
        });
      }
    },

    reset: () =>
      set({ integrators: [], ...emptyCoverage, loading: false, loadedCompanyId: null }),
  })
);
